import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Activity, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { useAgentTraces } from "@/hooks/useAgentDetail";
import type { AgentTracesParams, TelemetryTrace } from "@/services/aiAgentsService";

interface TracesTabProps {
  agentId: number;
}

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: "bg-red-100 text-red-800 border-red-200",
  HIGH: "bg-orange-100 text-orange-800 border-orange-200",
  MEDIUM: "bg-yellow-100 text-yellow-800 border-yellow-200",
  LOW: "bg-gray-100 text-gray-600 border-gray-200",
  INFO: "bg-blue-100 text-blue-600 border-blue-200",
};

const ITEMS_PER_PAGE = 20;

function TraceDetailRow({ trace }: { trace: TelemetryTrace }) {
  return (
    <TableRow className="bg-muted/30">
      <TableCell colSpan={5} className="py-3">
        <div className="space-y-2 text-xs font-mono">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-muted-foreground">Source ID: </span>
              <span>{trace.source_id}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Correlation ID: </span>
              <span>{trace.correlation_id ?? "—"}</span>
            </div>
          </div>
          {trace.tags.length > 0 && (
            <div>
              <span className="text-muted-foreground">Tags: </span>
              {trace.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="mr-1 rounded-[5px] text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
          {trace.event_metadata && Object.keys(trace.event_metadata).length > 0 && (
            <div>
              <span className="text-muted-foreground block mb-1">Metadata:</span>
              <pre className="max-h-40 overflow-x-auto rounded-[10px] bg-muted p-2 text-xs">
                {JSON.stringify(trace.event_metadata, null, 2)}
              </pre>
            </div>
          )}
          {trace.raw_data && Object.keys(trace.raw_data).length > 0 && (
            <details>
              <summary className="text-muted-foreground cursor-pointer hover:text-foreground">
                Raw data
              </summary>
              <pre className="mt-1 max-h-60 overflow-x-auto rounded-[10px] bg-muted p-2 text-xs">
                {JSON.stringify(trace.raw_data, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function TracesTab({ agentId }: TracesTabProps) {
  const [page, setPage] = useState(1);
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [eventTypeFilter] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const params: AgentTracesParams = useMemo(
    () => ({
      page,
      items_per_page: ITEMS_PER_PAGE,
      severity: severityFilter !== "all" ? severityFilter : undefined,
      event_type: eventTypeFilter !== "all" ? eventTypeFilter : undefined,
    }),
    [page, severityFilter, eventTypeFilter]
  );

  const { data: response, isLoading, error } = useAgentTraces(agentId, params);

  const traces = response?.data ?? [];
  const totalCount = response?.total_count ?? 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE) || 1;

  const handleFilterChange = () => {
    setPage(1);
  };

  return (
    <div className="space-y-4 pt-6">
      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select
          value={severityFilter}
          onValueChange={(v) => {
            setSeverityFilter(v);
            handleFilterChange();
          }}
        >
          <SelectTrigger className="w-[140px] rounded-full bg-white">
            <SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent className="rounded-[20px] bg-white">
            <SelectItem value="all">All Severities</SelectItem>
            <SelectItem value="CRITICAL">Critical</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
            <SelectItem value="INFO">Info</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto text-sm text-muted-foreground">
          {totalCount} trace{totalCount !== 1 ? "s" : ""}
        </div>
      </div>

      {/* Traces table */}
      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-[10px]" />
          ))}
        </div>
      )}

      {error && (
        <div className="text-center py-8 text-destructive">
          <p className="text-sm">Failed to load traces.</p>
        </div>
      )}

      {!isLoading && !error && traces.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Activity className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No telemetry traces found for this agent.</p>
        </div>
      )}

      {!isLoading && !error && traces.length > 0 && (
        <>
          <div className="overflow-hidden rounded-[15px] border bg-white">
            <Table>
              <TableHeader>
                <TableRow className="h-9">
                  <TableHead className="w-[190px] whitespace-nowrap py-2 text-xs font-medium">Timestamp</TableHead>
                  <TableHead className="py-2 text-xs font-medium">Event Type</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[100px]">Severity</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[100px] text-right">Anomaly</TableHead>
                  <TableHead className="py-2 text-xs font-medium w-[60px]">Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {traces.map((trace) => (
                  <>
                    <TableRow
                      key={trace.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => setExpandedId(expandedId === trace.id ? null : trace.id)}
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") setExpandedId(expandedId === trace.id ? null : trace.id);
                      }}
                    >
                      <TableCell className="whitespace-nowrap py-3">
                        <div className="flex items-center gap-1">
                          {expandedId === trace.id ? (
                            <ChevronUp className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-3 w-3 text-muted-foreground" />
                          )}
                          <span className="whitespace-nowrap font-mono text-xs">
                            {new Date(trace.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="text-sm">{trace.event_type}</span>
                      </TableCell>
                      <TableCell className="py-3">
                        <Badge className={`rounded-[5px] text-xs ${SEVERITY_COLORS[trace.severity] ?? ""}`}>
                          {trace.severity}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <span className="text-sm font-mono">
                          {trace.anomaly_score > 0 ? (trace.anomaly_score * 100).toFixed(0) + "%" : "—"}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="text-xs text-muted-foreground">{trace.source}</span>
                      </TableCell>
                    </TableRow>
                    {expandedId === trace.id && (
                      <TraceDetailRow key={`detail-${trace.id}`} trace={trace} />
                    )}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
